/**
 * One error shape for the whole application.
 *
 * The API always answers failures with
 * `{"error": {code, message, request_id, details}}`, so this turns an
 * AxiosError into a typed object and nothing downstream ever writes
 * `err.response.data.error.details` by hand.
 *
 * `code` is the discriminator worth switching on — it is stable, whereas HTTP
 * status codes are coarse (403 covers both "you lack the permission" and "that
 * site is not in your scope", which the UI should phrase differently).
 */
import { AxiosError } from 'axios';

/** Field name -> messages, as returned by DRF serializers. */
export type FieldErrors = Record<string, string[]>;

/**
 * A real Error subclass, not a plain object.
 *
 * Rejecting a promise with a bare object loses the stack trace, breaks
 * `instanceof`, and shows up as `[object Object]` in any logger that expects
 * an Error. React Query, error boundaries and Sentry all assume Error.
 */
export class ApiError extends Error {
  /** Stable identifier: `out_of_scope`, `validation_error`, `business_rule_violation`… */
  readonly code: string;
  /** Correlation id — quote it in a support ticket and the logs are findable. */
  readonly requestId: string;
  readonly details: Record<string, unknown>;
  readonly fieldErrors: FieldErrors;
  readonly status: number;
  /** True when the request never reached the server. */
  readonly isNetworkError: boolean;

  constructor(init: {
    code: string;
    message: string;
    requestId: string;
    details: Record<string, unknown>;
    fieldErrors: FieldErrors;
    status: number;
    isNetworkError: boolean;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details;
    this.fieldErrors = init.fieldErrors;
    this.status = init.status;
    this.isNetworkError = init.isNetworkError;
  }
}

interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
    request_id?: string;
    details?: Record<string, unknown>;
  };
}

function extractFieldErrors(details: Record<string, unknown>): FieldErrors {
  const fields: FieldErrors = {};
  for (const [key, value] of Object.entries(details)) {
    if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
      fields[key] = value;
    } else if (typeof value === 'string') {
      fields[key] = [value];
    }
  }
  return fields;
}

export function normalizeError(error: unknown): ApiError {
  // `apiClient`'s response interceptor already rejects with an ApiError, and
  // every screen normalises whatever it catches. Normalising has to be
  // idempotent: the fallback branch at the bottom would rebuild the error from
  // an ApiError as if it were a bare value, dropping `details` and
  // `fieldErrors` on the way. That is what silently turns a workbook's
  // list of rejected rows into one generic message.
  if (isApiError(error)) return error;

  if (error instanceof AxiosError) {
    if (!error.response) {
      return new ApiError({
        code: 'network_error',
        message: 'No se pudo conectar con el servidor. Revisa tu conexión.',
        requestId: '',
        details: {},
        fieldErrors: {},
        status: 0,
        isNetworkError: true,
      });
    }

    const payload = error.response.data as ApiErrorPayload | undefined;
    const body = payload?.error;
    const details = body?.details ?? {};

    return new ApiError({
      code: body?.code ?? 'unknown_error',
      message: body?.message ?? 'Ocurrió un error inesperado.',
      requestId: body?.request_id ?? '',
      details,
      fieldErrors: extractFieldErrors(details),
      status: error.response.status,
      isNetworkError: false,
    });
  }

  return new ApiError({
    code: 'unknown_error',
    message: error instanceof Error ? error.message : 'Ocurrió un error inesperado.',
    requestId: '',
    details: {},
    fieldErrors: {},
    status: 0,
    isNetworkError: false,
  });
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** True when the user is authenticated but this record is outside their scope (RN-24). */
export function isOutOfScope(error: unknown): boolean {
  return isApiError(error) && error.code === 'out_of_scope';
}

/** True when a rule from the functional manual was violated — `details.rule` holds the RN code. */
export function isBusinessRuleViolation(error: unknown): boolean {
  return isApiError(error) && error.code === 'business_rule_violation';
}
